'use client';

import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PrescriptionExercise {
  name: string;
  priority: 'HIGH' | 'MED' | 'LOW';
  difficulty: string;
  rationale: string;
  duration: string;
  targetDeficit: string;
}

export interface PrescriptionLifestyle {
  category: string;
  recommendation: string;
  evidence: string;
}

export interface AIPrescription {
  summary: string;
  urgencyLevel: 'ROUTINE' | 'ELEVATED' | 'CRITICAL';
  exercises: PrescriptionExercise[];
  lifestyle: PrescriptionLifestyle[];
  followUpRecommended: boolean;
  followUpReason?: string;
  generatedAt: string;
}

export interface AdaptiveProtocol {
  id: string;
  sessionId: string | null;
  subjectId: string;
  prescription: AIPrescription;
  alzheimerSeverity: string | null;
  alzheimerScore: number | null;
  adhdTbr: number | null;
  adhdClassification: string | null;
  avgCoherence: number | null;
  avgThetaAlphaRatio: number | null;
  avgAttentionScore: number | null;
  avgCognitiveLoad: number | null;
  status: 'active' | 'completed' | 'archived';
  createdAt: string;
  updatedAt: string;
  // Joined
  protocolSessions?: ProtocolSession[];
}

export interface ProtocolSession {
  id: string;
  protocolId: string;
  subjectId: string;
  interventionType: 'neurotrainer' | 'lifestyle' | 'sleep' | 'followup';
  exerciseName: string | null;
  difficultyLevel: string | null;
  durationSeconds: number;
  preAttentionScore: number | null;
  postAttentionScore: number | null;
  preCognitiveLoad: number | null;
  postCognitiveLoad: number | null;
  improvementDelta: number | null;
  notes: string | null;
  completedAt: string;
  createdAt: string;
}

export interface SaveProtocolParams {
  sessionId?: string;
  subjectId?: string;
  prescription: AIPrescription;
  alzheimerSeverity?: string;
  alzheimerScore?: number;
  adhdTbr?: number;
  adhdClassification?: string;
  avgCoherence?: number;
  avgThetaAlphaRatio?: number;
  avgAttentionScore?: number;
  avgCognitiveLoad?: number;
}

export interface LogProtocolSessionParams {
  protocolId: string;
  subjectId?: string;
  interventionType: 'neurotrainer' | 'lifestyle' | 'sleep' | 'followup';
  exerciseName?: string;
  difficultyLevel?: string;
  durationSeconds?: number;
  preAttentionScore?: number;
  postAttentionScore?: number;
  preCognitiveLoad?: number;
  postCognitiveLoad?: number;
  notes?: string;
}

// ─── Service ──────────────────────────────────────────────────────────────────

export const adaptiveProtocolService = {
  /**
   * Save a new AI-generated prescription protocol to Supabase.
   */
  async saveProtocol(params: SaveProtocolParams): Promise<string | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('adaptive_protocols')
        .insert({
          session_id: params.sessionId ?? null,
          subject_id: params.subjectId ?? 'anonymous',
          prescription: params.prescription as unknown as Record<string, unknown>,
          alzheimer_severity: params.alzheimerSeverity ?? null,
          alzheimer_score: params.alzheimerScore ?? null,
          adhd_tbr: params.adhdTbr ?? null,
          adhd_classification: params.adhdClassification ?? null,
          avg_coherence: params.avgCoherence ?? null,
          avg_theta_alpha_ratio: params.avgThetaAlphaRatio ?? null,
          avg_attention_score: params.avgAttentionScore ?? null,
          avg_cognitive_load: params.avgCognitiveLoad ?? null,
          status: 'active',
        })
        .select('id')
        .single();

      if (error) {
        console.warn('Failed to save adaptive protocol:', error.message);
        return null;
      }
      return data?.id ?? null;
    } catch (err) {
      console.warn('adaptiveProtocolService.saveProtocol error:', err);
      return null;
    }
  },

  /**
   * Fetch all protocols for a subject, newest first.
   */
  async getProtocols(subjectId: string = 'anonymous', limit = 20): Promise<AdaptiveProtocol[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('adaptive_protocols')
        .select('*, protocol_sessions(*)')
        .eq('subject_id', subjectId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.warn('Failed to fetch adaptive protocols:', error.message);
        return [];
      }

      return (data ?? []).map((row) => ({
        id: row.id,
        sessionId: row.session_id,
        subjectId: row.subject_id,
        prescription: row.prescription as unknown as AIPrescription,
        alzheimerSeverity: row.alzheimer_severity,
        alzheimerScore: row.alzheimer_score,
        adhdTbr: row.adhd_tbr,
        adhdClassification: row.adhd_classification,
        avgCoherence: row.avg_coherence,
        avgThetaAlphaRatio: row.avg_theta_alpha_ratio,
        avgAttentionScore: row.avg_attention_score,
        avgCognitiveLoad: row.avg_cognitive_load,
        status: row.status,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
        protocolSessions: (row.protocol_sessions ?? []).map((s: Record<string, unknown>) => ({
          id: s.id,
          protocolId: s.protocol_id,
          subjectId: s.subject_id,
          interventionType: s.intervention_type,
          exerciseName: s.exercise_name,
          difficultyLevel: s.difficulty_level,
          durationSeconds: s.duration_seconds,
          preAttentionScore: s.pre_attention_score,
          postAttentionScore: s.post_attention_score,
          preCognitiveLoad: s.pre_cognitive_load,
          postCognitiveLoad: s.post_cognitive_load,
          improvementDelta: s.improvement_delta,
          notes: s.notes,
          completedAt: s.completed_at,
          createdAt: s.created_at,
        })),
      }));
    } catch (err) {
      console.warn('adaptiveProtocolService.getProtocols error:', err);
      return [];
    }
  },

  /**
   * Log a completed intervention session against a protocol.
   */
  async logSession(params: LogProtocolSessionParams): Promise<string | null> {
    const supabase = createClient();
    try {
      const improvementDelta =
        params.postAttentionScore != null && params.preAttentionScore != null
          ? params.postAttentionScore - params.preAttentionScore
          : null;

      const { data, error } = await supabase
        .from('protocol_sessions')
        .insert({
          protocol_id: params.protocolId,
          subject_id: params.subjectId ?? 'anonymous',
          intervention_type: params.interventionType,
          exercise_name: params.exerciseName ?? null,
          difficulty_level: params.difficultyLevel ?? null,
          duration_seconds: params.durationSeconds ?? 0,
          pre_attention_score: params.preAttentionScore ?? null,
          post_attention_score: params.postAttentionScore ?? null,
          pre_cognitive_load: params.preCognitiveLoad ?? null,
          post_cognitive_load: params.postCognitiveLoad ?? null,
          improvement_delta: improvementDelta,
          notes: params.notes ?? null,
        })
        .select('id')
        .single();

      if (error) {
        console.warn('Failed to log protocol session:', error.message);
        return null;
      }
      return data?.id ?? null;
    } catch (err) {
      console.warn('adaptiveProtocolService.logSession error:', err);
      return null;
    }
  },

  /**
   * Update protocol status.
   */
  async updateStatus(protocolId: string, status: 'active' | 'completed' | 'archived'): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('adaptive_protocols')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', protocolId);

      if (error) {
        console.warn('Failed to update protocol status:', error.message);
        return false;
      }
      return true;
    } catch (err) {
      console.warn('adaptiveProtocolService.updateStatus error:', err);
      return false;
    }
  },
};
